import React, { useState, useEffect, useMemo } from 'react';
import { getApiBase } from '../api';
import { useAuth } from '../components/AuthContext';

function money(cents) {
  if (cents === null || cents === undefined) return '—';
  return `$${(Number(cents) / 100).toLocaleString('en-US', { minimumFractionDigits: 2 })}`;
}
function formatDate(dt) {
  if (!dt) return '—';
  return new Date(dt).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit'
  });
}

const statusConfig = {
  PENDING:  { label: '⏳ Pending Review', bg: '#fef3c7', text: '#92400e' },
  APPROVED: { label: '✓ Approved',        bg: '#dbeafe', text: '#1e40af' },
  FUNDED:   { label: '💵 Funded',          bg: '#d1fae5', text: '#065f46' },
  SETTLED:  { label: '✅ Settled',          bg: '#e0e7ff', text: '#3730a3' },
  REJECTED: { label: '✗ Rejected',        bg: '#fee2e2', text: '#991b1b' }
};

export default function AccountingDashboard() {
  const apiBase = useMemo(() => getApiBase(), []);
  const { adminEmail, role } = useAuth();
  const [stats, setStats] = useState(null);
  const [requests, setRequests] = useState([]);
  const [auditLog, setAuditLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('PENDING');
  const [actionLoading, setActionLoading] = useState(null);
  const [showAudit, setShowAudit] = useState(false);
  const [fundingModal, setFundingModal] = useState(null);
  const [fundingForm, setFundingForm] = useState({
    paymentMethod: 'ACH', paymentReference: '', notes: ''
  });
  const [rejectModal, setRejectModal] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  useEffect(() => { fetchData(); }, [filter]);

  async function fetchData() {
    setLoading(true);
    setError('');
    try {
      const [statsRes, requestsRes] = await Promise.all([
        fetch(`${apiBase}/accounting/dashboard`, {
          headers: { 'x-admin-email': adminEmail }
        }),
        fetch(`${apiBase}/accounting/requests?status=${filter}`, {
          headers: { 'x-admin-email': adminEmail }
        })
      ]);
      if (!statsRes.ok) throw new Error('Failed to fetch dashboard');
      if (!requestsRes.ok) throw new Error('Failed to fetch requests');
      setStats(await statsRes.json());
      setRequests((await requestsRes.json()).requests || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function fetchAuditLog() {
    try {
      const res = await fetch(`${apiBase}/accounting/audit-log`, {
        headers: { 'x-admin-email': adminEmail }
      });
      if (res.ok) setAuditLog((await res.json()).entries || []);
    } catch (_) {}
  }

  async function handleApprove(requestId) {
    if (!window.confirm('Approve this factoring request?\n\nGAAP Note: A different user must fund this request.')) return;
    setActionLoading(requestId);
    try {
      const res = await fetch(`${apiBase}/accounting/approve/${requestId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-admin-email': adminEmail }
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to approve');
      await fetchData();
    } catch (err) {
      alert(err.message);
    } finally {
      setActionLoading(null);
    }
  }

  async function handleReject(requestId) {
    setActionLoading(requestId);
    try {
      const res = await fetch(`${apiBase}/accounting/reject/${requestId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-admin-email': adminEmail },
        body: JSON.stringify({ reason: rejectReason })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to reject');
      setRejectModal(null);
      setRejectReason('');
      await fetchData();
    } catch (err) {
      alert(err.message);
    } finally {
      setActionLoading(null);
    }
  }

  async function handleFund() {
    if (!fundingModal) return;
    setActionLoading(fundingModal.id);
    try {
      const res = await fetch(`${apiBase}/accounting/fund/${fundingModal.id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-admin-email': adminEmail },
        body: JSON.stringify(fundingForm)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to fund');
      setFundingModal(null);
      setFundingForm({ paymentMethod: 'ACH', paymentReference: '', notes: '' });
      await fetchData();
    } catch (err) {
      alert(err.message);
    } finally {
      setActionLoading(null);
    }
  }

  async function handleSettle(requestId) {
    if (!window.confirm('Mark this request as settled?\n\nThis confirms payment was received by the carrier.')) return;
    setActionLoading(requestId);
    try {
      const res = await fetch(`${apiBase}/accounting/settle/${requestId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-admin-email': adminEmail }
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to settle');
      await fetchData();
    } catch (err) {
      alert(err.message);
    } finally {
      setActionLoading(null);
    }
  }

  if (loading && !stats) return <div style={{ padding: 40, textAlign: 'center' }}>Loading accounting dashboard...</div>;

  const isAccounting = role === 'ACCOUNTING';

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto' }}>
      {/* GAAP Compliance Banner */}
      <div style={{
        background: 'linear-gradient(135deg, #1e3a5f, #2d5a87)',
        borderRadius: 12, padding: '20px 24px', marginBottom: 24, color: 'white',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center'
      }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 900 }}>💼 Accounting Dashboard</h1>
          <p style={{ margin: '4px 0 0', fontSize: 13, opacity: 0.8 }}>
            GAAP-Compliant • Separation of Duties Enforced • Full Audit Trail
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => { setShowAudit(!showAudit); if (!showAudit) fetchAuditLog(); }}
            style={{ padding: '8px 16px', background: 'rgba(255,255,255,0.15)', color: 'white',
              border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>
            📋 {showAudit ? 'Hide' : 'View'} Audit Log
          </button>
        </div>
      </div>

      {!isAccounting && (
        <div style={{ padding: 16, background: '#fef3c7', border: '1px solid #f59e0b', borderRadius: 8, marginBottom: 16, fontSize: 14 }}>
          ⚠️ <strong>Read-Only Mode:</strong> You are viewing as {role}. Only users with the ACCOUNTING role can approve, fund, or settle requests.
        </div>
      )}

      {error && (
        <div style={{ padding: 12, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, color: '#991b1b', marginBottom: 16 }}>
          {error}
        </div>
      )}

      {/* Stats Cards */}
      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12, marginBottom: 24 }}>
          {[
            { label: 'Pending', value: stats.pending, color: '#f59e0b', icon: '⏳' },
            { label: 'Approved', value: stats.approved, color: '#3b82f6', icon: '✓' },
            { label: 'Funded', value: stats.funded, color: '#10b981', icon: '💵' },
            { label: 'Settled', value: stats.settled, color: '#6366f1', icon: '✅' },
            { label: 'Rejected', value: stats.rejected, color: '#ef4444', icon: '✗' },
          ].map(card => (
            <div key={card.label} onClick={() => setFilter(card.label.toUpperCase())}
              style={{
                background: filter === card.label.toUpperCase() ? card.color : 'white',
                color: filter === card.label.toUpperCase() ? 'white' : '#333',
                borderRadius: 10, padding: 16, cursor: 'pointer',
                border: `2px solid ${card.color}`, textAlign: 'center',
                transition: 'all 0.15s ease'
              }}>
              <div style={{ fontSize: 28, fontWeight: 800 }}>{card.value}</div>
              <div style={{ fontSize: 13, fontWeight: 600, marginTop: 4 }}>{card.icon} {card.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Financial Summary */}
      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 24 }}>
          <div style={{ background: '#f0fdf4', borderRadius: 10, padding: 16, border: '1px solid #bbf7d0' }}>
            <div style={{ fontSize: 13, color: '#166534', fontWeight: 600 }}>Total Advanced</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: '#166534' }}>{money(stats.totalAdvanceCents)}</div>
          </div>
          <div style={{ background: '#eff6ff', borderRadius: 10, padding: 16, border: '1px solid #bfdbfe' }}>
            <div style={{ fontSize: 13, color: '#1e40af', fontWeight: 600 }}>Total Fees Earned</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: '#1e40af' }}>{money(stats.totalFeeCents)}</div>
          </div>
          <div style={{ background: '#fef3c7', borderRadius: 10, padding: 16, border: '1px solid #fde68a' }}>
            <div style={{ fontSize: 13, color: '#92400e', fontWeight: 600 }}>Pending Exposure</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: '#92400e' }}>{money(stats.totalPendingCents)}</div>
          </div>
        </div>
      )}

      {/* Audit Log Panel */}
      {showAudit && (
        <div style={{ background: '#f8fafc', borderRadius: 12, border: '1px solid #e2e8f0', padding: 20, marginBottom: 24 }}>
          <h3 style={{ margin: '0 0 12px', fontSize: 16, fontWeight: 800 }}>📋 Audit Trail</h3>
          {auditLog.length === 0 ? (
            <p style={{ color: '#64748b', fontSize: 14 }}>No audit entries yet.</p>
          ) : (
            <div style={{ maxHeight: 300, overflow: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #e2e8f0' }}>
                    <th style={{ textAlign: 'left', padding: 8 }}>Timestamp</th>
                    <th style={{ textAlign: 'left', padding: 8 }}>User</th>
                    <th style={{ textAlign: 'left', padding: 8 }}>Action</th>
                    <th style={{ textAlign: 'left', padding: 8 }}>Request</th>
                    <th style={{ textAlign: 'left', padding: 8 }}>Details</th>
                  </tr>
                </thead>
                <tbody>
                  {auditLog.map((entry, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid #e2e8f0' }}>
                      <td style={{ padding: 8 }}>{formatDate(entry.timestamp)}</td>
                      <td style={{ padding: 8, fontWeight: 600 }}>{entry.userEmail}</td>
                      <td style={{ padding: 8 }}>
                        <span style={{
                          padding: '2px 8px', borderRadius: 4, fontSize: 12, fontWeight: 700,
                          background: entry.action === 'APPROVE' ? '#dbeafe' :
                                     entry.action === 'FUND' ? '#d1fae5' :
                                     entry.action === 'SETTLE' ? '#e0e7ff' :
                                     entry.action === 'REJECT' ? '#fee2e2' : '#f3f4f6',
                          color: entry.action === 'APPROVE' ? '#1e40af' :
                                 entry.action === 'FUND' ? '#065f46' :
                                 entry.action === 'SETTLE' ? '#3730a3' :
                                 entry.action === 'REJECT' ? '#991b1b' : '#374151'
                        }}>{entry.action}</span>
                      </td>
                      <td style={{ padding: 8, fontFamily: 'monospace', fontSize: 12 }}>{entry.requestId?.slice(0,8)}...</td>
                      <td style={{ padding: 8, color: '#64748b' }}>{entry.details || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Requests Table */}
      <div style={{ background: 'white', borderRadius: 12, border: '1px solid #e5e7eb', overflow: 'hidden' }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 800 }}>
            {filter} Requests ({requests.length})
          </h2>
          <div style={{ fontSize: 12, color: '#6b7280' }}>
            🔒 Separation of Duties: Approver ≠ Funder ≠ Settler
          </div>
        </div>

        {requests.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>
            No {filter.toLowerCase()} requests
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                <th style={{ textAlign: 'left', padding: '10px 16px', fontSize: 12, fontWeight: 700, color: '#6b7280' }}>LOAD</th>
                <th style={{ textAlign: 'left', padding: '10px 16px', fontSize: 12, fontWeight: 700, color: '#6b7280' }}>CARRIER</th>
                <th style={{ textAlign: 'right', padding: '10px 16px', fontSize: 12, fontWeight: 700, color: '#6b7280' }}>AMOUNT</th>
                <th style={{ textAlign: 'right', padding: '10px 16px', fontSize: 12, fontWeight: 700, color: '#6b7280' }}>ADVANCE</th>
                <th style={{ textAlign: 'right', padding: '10px 16px', fontSize: 12, fontWeight: 700, color: '#6b7280' }}>FEE</th>
                <th style={{ textAlign: 'left', padding: '10px 16px', fontSize: 12, fontWeight: 700, color: '#6b7280' }}>STATUS</th>
                <th style={{ textAlign: 'left', padding: '10px 16px', fontSize: 12, fontWeight: 700, color: '#6b7280' }}>AUDIT</th>
                {isAccounting && <th style={{ textAlign: 'right', padding: '10px 16px', fontSize: 12, fontWeight: 700, color: '#6b7280' }}>ACTIONS</th>}
              </tr>
            </thead>
            <tbody>
              {requests.map(req => {
                const sc = statusConfig[req.status] || {};
                const canApprove = isAccounting && req.status === 'PENDING';
                const canFund = isAccounting && req.status === 'APPROVED' && req.approvedByEmail !== adminEmail;
                const canSettle = isAccounting && req.status === 'FUNDED' && req.fundedByEmail !== adminEmail;
                const canReject = isAccounting && (req.status === 'PENDING' || req.status === 'APPROVED');
                const sameUserBlock = isAccounting && (
                  (req.status === 'APPROVED' && req.approvedByEmail === adminEmail) ||
                  (req.status === 'FUNDED' && req.fundedByEmail === adminEmail)
                );

                return (
                  <tr key={req.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '12px 16px', fontWeight: 600, fontSize: 14 }}>
                      {req.loadNumber || req.loadId?.slice(0,8) || '—'}
                    </td>
                    <td style={{ padding: '12px 16px', fontSize: 14 }}>{req.carrierName || '—'}</td>
                    <td style={{ padding: '12px 16px', textAlign: 'right', fontWeight: 600 }}>{money(req.invoiceAmountCents)}</td>
                    <td style={{ padding: '12px 16px', textAlign: 'right', fontWeight: 600, color: '#059669' }}>{money(req.advanceAmountCents)}</td>
                    <td style={{ padding: '12px 16px', textAlign: 'right', fontSize: 13, color: '#6b7280' }}>
                      {money(req.feeCents)} <span style={{ fontSize: 11 }}>({req.feePercent}%)</span>
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <span style={{ padding: '3px 10px', borderRadius: 6, fontSize: 12, fontWeight: 700, background: sc.bg, color: sc.text }}>
                        {sc.label}
                      </span>
                    </td>
                    <td style={{ padding: '12px 16px', fontSize: 11, color: '#6b7280', lineHeight: 1.6 }}>
                      {req.requestedAt && <div>📋 {formatDate(req.requestedAt)}</div>}
                      {req.approvedByEmail && <div>✓ {req.approvedByEmail}</div>}
                      {req.fundedByEmail && <div>💵 {req.fundedByEmail}</div>}
                      {req.settledByEmail && <div>✅ {req.settledByEmail}</div>}
                    </td>
                    {isAccounting && (
                      <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                        <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                          {canApprove && (
                            <button onClick={() => handleApprove(req.id)}
                              disabled={actionLoading === req.id}
                              style={{ padding: '6px 12px', background: '#3b82f6', color: 'white', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                              ✓ Approve
                            </button>
                          )}
                          {canFund && (
                            <button onClick={() => setFundingModal(req)}
                              disabled={actionLoading === req.id}
                              style={{ padding: '6px 12px', background: '#10b981', color: 'white', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                              💵 Fund
                            </button>
                          )}
                          {canSettle && (
                            <button onClick={() => handleSettle(req.id)}
                              disabled={actionLoading === req.id}
                              style={{ padding: '6px 12px', background: '#6366f1', color: 'white', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                              ✅ Settle
                            </button>
                          )}
                          {canReject && (
                            <button onClick={() => setRejectModal(req)}
                              disabled={actionLoading === req.id}
                              style={{ padding: '6px 12px', background: '#ef4444', color: 'white', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                              ✗ Reject
                            </button>
                          )}
                          {sameUserBlock && (
                            <span style={{ fontSize: 11, color: '#dc2626', fontWeight: 600, padding: '6px 0' }}>
                              🔒 Different user required
                            </span>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* GAAP Footer */}
      <div style={{ marginTop: 16, padding: 16, background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 12, color: '#64748b' }}>
        <strong>GAAP Compliance Notes:</strong>
        <div style={{ marginTop: 4 }}>
          • <strong>Separation of Duties:</strong> The user who approves cannot fund. The user who funds cannot settle.
        </div>
        <div>• <strong>Audit Trail:</strong> Every approval, funding, and settlement is logged with user identity and timestamp.</div>
        <div>• <strong>Access Control:</strong> Only ACCOUNTING role users can perform financial actions. Brokers have read-only access.</div>
        <div>• <strong>Dual Authorization:</strong> All financial disbursements require approval from a separate authorized user.</div>
      </div>

      {/* Funding Modal */}
      {fundingModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: 'white', borderRadius: 16, padding: 32, width: 450, maxHeight: '80vh', overflow: 'auto' }}>
            <h3 style={{ margin: '0 0 4px', fontSize: 18, fontWeight: 800 }}>💵 Fund Request</h3>
            <p style={{ margin: '0 0 20px', fontSize: 13, color: '#6b7280' }}>
              Load: <strong>{fundingModal.loadNumber || '—'}</strong> • Advance: <strong>{money(fundingModal.advanceAmountCents)}</strong>
            </p>
            
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4 }}>Payment Method</label>
              <select value={fundingForm.paymentMethod} onChange={e => setFundingForm({...fundingForm, paymentMethod: e.target.value})}
                style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14 }}>
                <option value="ACH">ACH Transfer</option>
                <option value="WIRE">Wire Transfer</option>
                <option value="CHECK">Check</option>
              </select>
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4 }}>Payment Reference / Confirmation #</label>
              <input value={fundingForm.paymentReference} onChange={e => setFundingForm({...fundingForm, paymentReference: e.target.value})}
                placeholder="e.g. ACH-2026-0001"
                style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14, boxSizing: 'border-box' }} />
            </div>
            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4 }}>Notes</label>
              <textarea rows={2} value={fundingForm.notes} onChange={e => setFundingForm({...fundingForm, notes: e.target.value})}
                style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14, resize: 'vertical', boxSizing: 'border-box' }} />
            </div>

            <div style={{ padding: 12, background: '#fef3c7', borderRadius: 8, marginBottom: 16, fontSize: 12, color: '#92400e' }}>
              🔒 <strong>GAAP Notice:</strong> This funding action is being logged. Approved by: <strong>{fundingModal.approvedByEmail || 'system'}</strong>.
              You ({adminEmail}) are authorizing the disbursement.
            </div>

            <div style={{ display: 'flex', gap: 12 }}>
              <button onClick={handleFund}
                disabled={actionLoading === fundingModal.id}
                style={{ flex: 1, padding: 12, background: '#10b981', color: 'white', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer' }}>
                {actionLoading === fundingModal.id ? 'Processing...' : '💵 Confirm Funding'}
              </button>
              <button onClick={() => setFundingModal(null)}
                style={{ padding: '12px 24px', background: '#f3f4f6', color: '#374151', border: 'none', borderRadius: 8, fontWeight: 600, cursor: 'pointer' }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {rejectModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: 'white', borderRadius: 16, padding: 32, width: 400 }}>
            <h3 style={{ margin: '0 0 16px', fontSize: 18, fontWeight: 800, color: '#991b1b' }}>✗ Reject Request</h3>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4 }}>Reason for Rejection *</label>
              <textarea rows={3} value={rejectReason} onChange={e => setRejectReason(e.target.value)}
                placeholder="Provide a reason for the rejection..."
                style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14, resize: 'vertical', boxSizing: 'border-box' }} />
            </div>
            <div style={{ display: 'flex', gap: 12 }}>
              <button onClick={() => handleReject(rejectModal.id)}
                disabled={!rejectReason.trim() || actionLoading === rejectModal.id}
                style={{ flex: 1, padding: 12, background: rejectReason.trim() ? '#ef4444' : '#d1d5db', color: 'white', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer' }}>
                Confirm Rejection
              </button>
              <button onClick={() => { setRejectModal(null); setRejectReason(''); }}
                style={{ padding: '12px 24px', background: '#f3f4f6', color: '#374151', border: 'none', borderRadius: 8, fontWeight: 600, cursor: 'pointer' }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
